import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { useNotification } from '../../../hooks/NotificationContext';
import {
  createFilterPreset,
  deleteFilterPreset,
  updateFilterPreset,
} from '../../../modules/datafilters';
import Modal from '../../layout/Modal';
import ActionButton from '../../primitives/ActionButton';
import FilterField from './FilterField';
import SavePresetPopover from './SavePresetPopover';
import { buildDefaultFilters } from './filterUtils';

const filtersEqual = (a, b) => isEqual(a, b);

const FilterModal = ({
  open,
  onClose,
  onApply,
  filterConfig = [],
  currentFilters = {},
  optionsMap = {},
  title = 'Filters',
  showExecutionScope = false,
  ExecutionScopeComponent = null,
  onExecutionScopeChange,
  savedFilters = [],
  presetWarning = null,
  userRole = 'member',
  currentUserId,
  refreshPresets,
  projectId,
  filterType = 'testcase',
}) => {
  const [localFilters, setLocalFilters] = useState(currentFilters);
  const [activePresetId, setActivePresetId] = useState(null);
  const [presetSnapshot, setPresetSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);

  const [presetMenuAnchor, setPresetMenuAnchor] = useState(null);
  const [savePopoverAnchor, setSavePopoverAnchor] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const saveButtonRef = useRef(null);
  const { addNotification } = useNotification();

  const hasSavedFilters =
    Array.isArray(savedFilters) && savedFilters.length > 0;

  const activePreset =
    savedFilters.find((p) => p.id === activePresetId) ?? null;
  const isOwnedActivePreset =
    activePreset !== null && activePreset.createdBy === currentUserId;
  const isDirty = useMemo(() => {
    if (!activePreset || !presetSnapshot) return false;
    return !filtersEqual(localFilters, presetSnapshot);
  }, [localFilters, presetSnapshot, activePreset]);
  const popoverMode = isOwnedActivePreset ? 'update' : 'save';

  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setLocalFilters(currentFilters);
      setActivePresetId(null);
      setPresetSnapshot(null);
    }
    wasOpen.current = open;
  }, [open, currentFilters]);

  const handleFieldChange = (key, value) => {
    setLocalFilters((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'status' && 'reviewer' in prev) updated.reviewer = [];
      return updated;
    });
  };

  const handleReset = () => {
    setLocalFilters(buildDefaultFilters(filterConfig));
    setActivePresetId(null);
    setPresetSnapshot(null);
    setWarningDismissed(false);
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleLoadPreset = (preset) => {
    const merged = {
      ...buildDefaultFilters(filterConfig),
      ...preset.filters,
    };
    setLocalFilters(merged);
    setActivePresetId(preset.id);
    setPresetSnapshot(merged);
    setPresetMenuAnchor(null);
    setWarningDismissed(false);
  };

  const handleDeletePreset = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteFilterPreset(projectId, id);
      addNotification('success', 'Filter preset deleted successfully!');
      await refreshPresets?.();

      if (activePresetId === id) {
        setActivePresetId(null);
        setPresetSnapshot(null);
      }
    } catch (err) {
      addNotification(
        'danger',
        err?.message || 'Failed to delete filter preset.',
      );
    }
  };

  const handleSavePreset = async (name, shareLevel, isUpdate) => {
    setSaveError(null);
    setSaving(true);

    try {
      if (isUpdate && activePresetId) {
        await updateFilterPreset(projectId, activePresetId, {
          name,
          filters: localFilters,
          shareLevel,
        });

        setPresetSnapshot(localFilters);

        addNotification('success', 'Filter preset updated successfully!');

        await refreshPresets?.();
      } else {
        const newPreset = await createFilterPreset(projectId, {
          name,
          filters: localFilters,
          shareLevel,
          filterType,
        });

        await refreshPresets?.();

        setActivePresetId(newPreset.id);
        setPresetSnapshot(localFilters);
      }

      setSavePopoverAnchor(null);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  const hasActiveFilters = useMemo(() => {
  const defaultFilters = buildDefaultFilters(filterConfig);
  return !filtersEqual(localFilters, defaultFilters);
}, [localFilters, filterConfig]);

  const visibleFields = filterConfig.filter(
    (field) =>
      !field.hiddenField &&
      (typeof field.visibleWhen === 'function'
        ? field.visibleWhen(localFilters)
        : true),
  );

  const saveTooltip = (() => {
    if (isOwnedActivePreset && isDirty)
      return `Save changes to "${activePreset.name}" or create a new filter`;
    if (activePreset && !isOwnedActivePreset)
      return 'Save current filters as a new preset';
    return 'Save current filters as a preset';
  })();

  const renderActions = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      gap={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <ActionButton
          intent="ghost"
          size="small"
          icon={<RestartAltIcon fontSize="small" />}
          label="Reset All"
          onClick={handleReset}
          tooltip="Reset all filters to defaults"
        />
        {(hasActiveFilters || isDirty || isOwnedActivePreset) && (
          <>
            <Box ref={saveButtonRef} display="inline-flex">
              <ActionButton
                intent="ghost"
                size="small"
                icon={<BookmarkAddOutlinedIcon fontSize="small" />}
                label="Save Filter"
                onClick={() => {
                  setSaveError(null);
                  setSavePopoverAnchor(saveButtonRef.current);
                }}
                tooltip={saveTooltip}
              />
            </Box>
            <SavePresetPopover
              open={Boolean(savePopoverAnchor)}
              anchorEl={savePopoverAnchor}
              onClose={() => setSavePopoverAnchor(null)}
              onSave={handleSavePreset}
              saving={saving}
              error={saveError}
              userRole={userRole}
              mode={popoverMode}
              initialName={activePreset?.name ?? ''}
              initialShareLevel={activePreset?.shareLevel ?? 'private'}
            />
          </>
        )}
      </Box>
      <Box display="flex" gap={1}>
        <ActionButton
          intent="secondary"
          size="small"
          label="Cancel"
          onClick={onClose}
        />
        <ActionButton
          intent="primary"
          size="small"
          label="Apply Filters"
          onClick={handleApply}
        />
      </Box>
    </Box>
  );

  return (
    <Modal
      open={open}
      handleClose={onClose}
      modalTitle={title}
      titleIcon={<FilterAltOutlinedIcon fontSize="small" />}
      width="md"
      disableBackdropClose
      contentSx={{ overflow: 'auto', px: 3, py: 2.5 }}
      action={renderActions}>
      {/* ── Saved filters dropdown ── */}
      {hasSavedFilters && (
        <Box
          mb={2.5}
          display="flex"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            color={activePreset ? 'primary' : 'inherit'}
            startIcon={
              <BookmarksOutlinedIcon sx={{ fontSize: '16px !important' }} />
            }
            endIcon={
              <KeyboardArrowDownIcon sx={{ fontSize: '16px !important' }} />
            }
            onClick={(e) => setPresetMenuAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none',
              fontWeight: activePreset ? 600 : 400,
              borderColor: activePreset ? 'primary.main' : 'divider',
              color: activePreset ? 'primary.main' : 'text.secondary',
              bgcolor: activePreset ? 'primary.50' : 'transparent',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
            }}>
            {activePreset
              ? isDirty
                ? `${activePreset.name} (modified)`
                : activePreset.name
              : 'Saved Filters'}
          </Button>

          <Menu
            anchorEl={presetMenuAnchor}
            open={Boolean(presetMenuAnchor)}
            onClose={() => setPresetMenuAnchor(null)}
            PaperProps={{
              elevation: 3,
              sx: { minWidth: 240, maxHeight: 320, borderRadius: 1.5 },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {savedFilters.length === 0 ? (
              <MenuItem disabled>
                <ListItemText
                  primary="No saved filters yet"
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.disabled',
                  }}
                />
              </MenuItem>
            ) : (
              savedFilters.map((preset) => {
                const isActive = activePresetId === preset.id;
                const isOwned = preset.createdBy === currentUserId;
                return (
                  <MenuItem
                    key={preset.id}
                    onClick={() => handleLoadPreset(preset)}
                    selected={isActive}
                    sx={{ pr: 1, gap: 1 }}>
                    <ListItemText
                      primary={preset.name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isActive ? 600 : 400,
                        noWrap: true,
                      }}
                    />
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      flexShrink={0}>
                      {isActive && (
                        <CheckIcon
                          sx={{ fontSize: 14, color: 'primary.main' }}
                        />
                      )}
                      {isOwned && (
                        <Tooltip title="Delete filter" placement="right">
                          <Box
                            component="span"
                            onClick={(e) => handleDeletePreset(e, preset.id)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: '50%',
                              p: 0.25,
                              color: 'text.disabled',
                              cursor: 'pointer',
                              '&:hover': {
                                color: 'error.main',
                                bgcolor: 'error.50',
                              },
                            }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </Menu>

          {presetWarning && !warningDismissed && (
            <Alert
              severity="warning"
              onClose={() => setWarningDismissed(true)}
              variant="outlined"
              sx={{
                py: 0,
                px: 1.5,
                fontSize: '0.8rem',
                borderRadius: 1.5,
                '& .MuiAlert-icon': { fontSize: 16 },
                '& .MuiAlert-action': { pt: 0 },
              }}>
              {presetWarning}
            </Alert>
          )}
        </Box>
      )}

      {/* ── Filter fields ── */}
      <Grid container spacing={2.5}>
        {visibleFields.map((field) => (
          <Grid item xs={12} sm={field.gridSize || 4} key={field.key}>
            <FilterField
              field={field}
              value={localFilters[field.key]}
              onChange={handleFieldChange}
              optionsMap={optionsMap}
            />
          </Grid>
        ))}
      </Grid>

      {showExecutionScope && ExecutionScopeComponent && (
        <>
          <Divider sx={{ my: 3 }} />
          {React.cloneElement(ExecutionScopeComponent, {
            values: localFilters,
            setFieldValue: (field, value) => {
              handleFieldChange(field, value);
              onExecutionScopeChange?.(field, value);
            },
            isPlatformRequired: false,
          })}
        </>
      )}
    </Modal>
  );
};

export default FilterModal;